/**
 * Dashboard Filters API
 *
 * API client functions for fetching filter options (job categories, users)
 */

import { apiClient } from '@/lib/api';

export interface JobCategory {
  id: number;
  name: string;
  job_audit_form_id: number | null;
  created_time: string;
  client_id: number;
}

export interface JobCategoryResponse {
  message: string;
  status: boolean;
  values: JobCategory[];
}

export interface User {
  id: number;
  username: string;
  cognito_username: string;
  first_name: string;
  second_name: string;
  preferred_name: string | null;
  job_title: string | null;
  full_name: string;
  name: string;
  update_username: string | null;
}

export interface UserResponse {
  message: string;
  count: number;
  values: User[];
}

export interface Priority {
  name: string;
  id: number;
}

export interface PriorityResponse {
  message: string;
  status: boolean;
  values: Priority[];
}

/**
 * Fetch job categories for filter dropdown
 *
 * @param search - Optional search query
 * @param limit - Number of results to return
 * @returns Job categories data
 */
export async function getJobCategories(search?: string, limit = 100): Promise<JobCategoryResponse> {
  const params = new URLSearchParams();
  if (search) params.append('search', search);
  params.append('limit', String(limit));
  params.append('skip', '0');

  const queryString = params.toString();
  const response = await apiClient.get<JobCategoryResponse>(
    `/job-category/list${queryString ? `?${queryString}` : ''}`,
  );

  // Deduplicate categories by ID (backend JOIN may return duplicates)
  const uniqueCategories = Array.from(
    new Map(response.values.map((category) => [category.id, category])).values(),
  );

  return {
    ...response,
    values: uniqueCategories,
  };
}

/**
 * Fetch users for assignee filter
 * Returns active, verified users only
 *
 * @returns User list data
 */
export async function getUsers(): Promise<UserResponse> {
  return apiClient.get<UserResponse>('/user/simple-list');
}

/**
 * Fetch priorities for the priority filter.
 *
 * Sourced from `/jobs/job-priorities` (the real `job_priority` table), the same
 * endpoint the Jobs list filter uses — NOT `/job/priority/list`, which returns a
 * hard-coded constant whose ids are a different vocabulary entirely (SC-6616).
 *
 * The two disagree and are close to inverted: the constant reads
 * 1=Low, 2=Medium, 3=High, 4=Urgent, while the table holds
 * 1=Critical, 2=High, 3=Medium, 4=Low, 5=Unknown. The dashboard sent the
 * constant's id and the analytics queries compared it against job data keyed on
 * the table's, so the filter matched nothing and silently zeroed every chart and
 * tile. Matching them up the wrong way round would have been worse still —
 * selecting "Low" would have returned Critical jobs.
 *
 * @returns Priority list data
 */
export async function getPriorities(): Promise<PriorityResponse> {
  const priorities = await apiClient.get<Priority[]>('/jobs/job-priorities');
  // Keep the envelope the dashboard hook already expects.
  return { message: 'Priorities retrieved successfully', status: true, values: priorities };
}
